import { memo, useMemo, useState } from 'react';
import { CheckCircle2, Clock, Loader2, XCircle } from 'lucide-react';
import { Badge, TabBar } from '@thegpvc/ui';
import type { TaskDetail } from '../types/api';
import { SectionCard } from './SectionCard';
import { formatInt, formatTimestamp } from '../utils/format';
import { text, table, border, cx } from '../styles/theme';
// Note: metadata labels use `min-w-32` (the colWidth.labelMd numeric scale as a
// min-width) so the label column holds a consistent width without shrinking.

function formatTaskType(taskType: string): string {
  const labels: Record<string, string> = {
    link_session: 'Link Session',
    extract_stats: 'Extract Stats',
    summarize_session: 'Summarize Session',
  };
  return labels[taskType] || taskType;
}

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  pending: 'neutral',
  processing: 'info',
  completed: 'success',
  failed: 'error',
  preempted: 'warning',
};

function statusIcon(status: string) {
  switch (status) {
    case 'pending':
      return <Clock className="w-3.5 h-3.5" />;
    case 'processing':
      return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    case 'completed':
      return <CheckCircle2 className="w-3.5 h-3.5" />;
    case 'failed':
    case 'preempted':
      return <XCircle className="w-3.5 h-3.5" />;
    default:
      return undefined;
  }
}

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'processing', label: 'Processing' },
  { id: 'completed', label: 'Completed' },
  { id: 'failed', label: 'Failed' },
];

const TYPE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'link_session', label: 'Link Session' },
  { id: 'extract_stats', label: 'Extract Stats' },
  { id: 'summarize_session', label: 'Summarize Session' },
];

interface TaskListProps {
  tasks: TaskDetail[];
  total: string;
}

export function TaskList({ tasks, total }: TaskListProps) {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const filteredTasks = useMemo(() =>
    tasks.filter((task) => {
      if (statusFilter !== 'all' && task.status !== statusFilter) return false;
      if (typeFilter !== 'all' && task.taskType !== typeFilter) return false;
      return true;
    }),
    [tasks, statusFilter, typeFilter]
  );

  return (
    <SectionCard
      title="Recent Tasks"
      flush
      action={
        <span className={cx('text-xs font-mono tabular-nums', text.muted)}>
          {filteredTasks.length} of {formatInt(total)} tasks
        </span>
      }
    >
      {/* Filters */}
      <div className={cx('flex flex-wrap items-center gap-x-6 gap-y-2 px-4 pb-3 border-b', border.default)}>
        <div className="flex items-center gap-2">
          <span className={cx('text-xs font-medium uppercase', text.muted)}>Status</span>
          <TabBar variant="pill" items={STATUS_FILTERS} activeId={statusFilter} onChange={setStatusFilter} />
        </div>
        <div className="flex items-center gap-2">
          <span className={cx('text-xs font-medium uppercase', text.muted)}>Type</span>
          <TabBar variant="pill" items={TYPE_FILTERS} activeId={typeFilter} onChange={setTypeFilter} />
        </div>
      </div>

      {/* Task rows */}
      <div className={cx('divide-y', table.divideMuted)}>
        {filteredTasks.length === 0 ? (
          <div className="p-12 text-center">
            <p className={text.secondary}>No tasks found</p>
          </div>
        ) : (
          filteredTasks.map((task) => <TaskRow key={task.id} task={task} />)
        )}
      </div>
    </SectionCard>
  );
}

interface TaskRowProps {
  task: TaskDetail;
}

const TaskRow = memo(function TaskRow({ task }: TaskRowProps) {
  const [expanded, setExpanded] = useState(false);

  const duration =
    parseInt(task.completedAt) > 0 && parseInt(task.createdAt) > 0
      ? ((parseInt(task.completedAt) - parseInt(task.createdAt)) / 1000).toFixed(1)
      : null;

  return (
    <div className={cx('p-4 transition-colors', table.rowHover)}>
      <div
        className={cx(
          'flex items-start gap-4 cursor-pointer',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-400',
        )}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(!expanded);
          }
        }}
      >
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <Badge variant={STATUS_VARIANT[task.status] ?? 'neutral'} icon={statusIcon(task.status)}>
              {task.status}
            </Badge>
            <span className={cx('text-sm font-medium', text.primary)}>
              {formatTaskType(task.taskType)}
            </span>
            {duration && (
              <span className={cx('text-xs font-mono tabular-nums', text.muted)}>{duration}s</span>
            )}
          </div>

          <p className={cx('text-sm truncate', text.secondary)}>{task.filePath}</p>

          {task.lastError && (
            <p className="text-xs text-rose-600 dark:text-rose-400 mt-1 font-mono truncate">
              {task.lastError}
            </p>
          )}

          {/* Metadata */}
          <div className={cx('flex items-center gap-3 mt-2 text-xs', text.muted)}>
            <span>Created {formatTimestamp(task.createdAt)}</span>
            {task.attempts > 1 && (
              <span className="text-orange-600 dark:text-orange-400 font-mono tabular-nums">
                Attempt {task.attempts}/{task.maxAttempts}
              </span>
            )}
            {task.projectName && <span>• {task.projectName}</span>}
          </div>
        </div>

        {/* Expand Indicator */}
        <div className="flex-shrink-0">
          <div
            className={cx('transform transition-transform', text.muted, expanded && 'rotate-180')}
            aria-hidden="true"
          >
            ▼
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-4 pl-2 space-y-2 text-sm">
          <DetailRow label="Task ID" value={task.id} mono />
          <DetailRow label="Upload ID" value={task.uploadId} mono />
          {task.dependsOn && <DetailRow label="Depends On" value={task.dependsOn} mono />}
          <TimestampRow label="Created At" timestamp={task.createdAt} />
          {task.claimedBy && (
            <>
              <DetailRow label="Claimed By" value={task.claimedBy} mono />
              <TimestampRow label="Claimed At" timestamp={task.claimedAt} />
            </>
          )}
          {parseInt(task.completedAt) > 0 && (
            <TimestampRow label="Completed At" timestamp={task.completedAt} />
          )}
          <DetailRow label="Hostname" value={task.hostname} />
          <DetailRow label="User" value={task.userEmail} />
        </div>
      )}
    </div>
  );
});

interface DetailRowProps {
  label: string;
  value: string;
  mono?: boolean;
}

function DetailRow({ label, value, mono }: DetailRowProps) {
  return (
    <div className="flex items-start gap-2">
      <span className={cx('font-medium min-w-32', text.secondary)}>{label}:</span>
      <span className={cx(mono ? 'font-mono text-xs' : '', 'break-all', text.primary)}>{value}</span>
    </div>
  );
}

interface TimestampRowProps {
  label: string;
  timestamp: string;
}

function TimestampRow({ label, timestamp }: TimestampRowProps) {
  const ts = parseInt(timestamp, 10);
  if (isNaN(ts) || ts === 0) {
    return null;
  }

  const date = new Date(ts * 1000);
  const absolute = date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const relative = formatTimestamp(timestamp);

  return (
    <div className="flex items-start gap-2">
      <span className={cx('font-medium min-w-32', text.secondary)}>{label}:</span>
      <span className={text.primary}>
        {relative} <span className={text.muted}>({absolute})</span>
      </span>
    </div>
  );
}
